import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth, useToast } from '../contexts/AppContexts';
import { ArrowLeft, CheckCircle, ClipboardList, Edit, MapPin, Printer, X } from 'lucide-react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Label } from './ui/label';
import { API_BASE_URL } from '../config';
import { writeSanitizedHTML } from '../utils/safePrint';
import { fetchHiraThumbnail } from '../utils/hiraThumbnails';

const API = `${API_BASE_URL}/api`;

const ExerciseDetailView = ({ exercise, onBack }) => {
  const { token } = useAuth();
  const toast = useToast();
  const [showMSELModal, setShowMSELModal] = useState(false);
  const [mselInjections, setMselInjections] = useState([]);
  const [loadingMSEL, setLoadingMSEL] = useState(false);
  // Lazy-loaded HIRA hazard image for the exercise header (only used when
  // the exercise itself has no exercise_image).
  const [hiraImage, setHiraImage] = useState(null);

  useEffect(() => {
    if (exercise?.exercise_image || !exercise?.hira_id) { setHiraImage(null); return; }
    let cancelled = false;
    (async () => {
      const image = await fetchHiraThumbnail(exercise.hira_id);
      if (!cancelled) setHiraImage(image);
    })();
    return () => { cancelled = true; };
  }, [exercise?.hira_id, exercise?.exercise_image]);

  // Fetch MSEL injections
  const handleViewMSEL = async () => {
    setLoadingMSEL(true);
    setShowMSELModal(true);
    
    try {
      const headers = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
      const response = await axios.get(`${API}/msel/${exercise.id}`, headers);
      const injections = response.data || [];
      // Sort injections by event_number
      const sortedInjections = injections.sort((a, b) => a.event_number - b.event_number);
      setMselInjections(sortedInjections);
      
      if (sortedInjections.length === 0) {
        toast.showToast('No injections found for this exercise', 'info');
      }
    } catch (error) {
      console.error('Error fetching MSEL injections:', error);
      toast.showToast('Failed to load MSEL injections', 'error');
      setMselInjections([]);
    } finally {
      setLoadingMSEL(false);
    }
  };

  const handlePrintMSEL = () => {
    if (!mselInjections.length) {
      toast.showToast('No MSEL injections to print', 'error');
      return;
    }
    const escapeHTML = (s) => String(s ?? '')
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
    const formatScenarioTime = (v) => {
      if (!v) return 'Not Set';
      try {
        return new Date(v).toLocaleString([], {
          month: 'short', day: 'numeric', year: 'numeric',
          hour: '2-digit', minute: '2-digit', hour12: false
        });
      } catch { return String(v); }
    };
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      toast.showToast('Please allow pop-ups to print the MSEL report.', 'error');
      return;
    }
    const rowsHTML = mselInjections.map((event) => `
      <div class="event">
        <div class="event-head">
          <div class="event-num">#${escapeHTML(event.event_number)}</div>
          <div class="event-title">
            <h2>Event #${escapeHTML(event.event_number)}${event.msel_name ? ` — ${escapeHTML(event.msel_name)}` : ''}</h2>
            <div class="badges">
              <span class="badge badge-type">${escapeHTML(event.event_type || 'No Event Type')}</span>
              <span class="badge badge-mode">${escapeHTML(event.inject_mode || 'No Inject Mode')}</span>
              ${event.completed ? '<span class="badge badge-done">Completed</span>' : ''}
            </div>
          </div>
        </div>
        <table class="event-table">
          <tbody>
            <tr><th>Scenario Date/Time</th><td>${escapeHTML(formatScenarioTime(event.scenario_time))}</td></tr>
            <tr><th>From → To</th><td>${escapeHTML(event.from_entity || 'Not Set')} → ${escapeHTML(event.to_entity || 'Not Set')}</td></tr>
            <tr><th>Message</th><td>${escapeHTML(event.message || '')}</td></tr>
            <tr><th>Expected Response</th><td>${escapeHTML(event.expected_response || '')}</td></tr>
            <tr><th>Objective / Task</th><td>${escapeHTML(event.objective_capability_task || '')}</td></tr>
            ${event.notes ? `<tr><th>Controller Notes</th><td>${escapeHTML(event.notes)}</td></tr>` : ''}
            ${event.completed && event.actual_time ? `<tr><th>Completed At</th><td>${escapeHTML(event.actual_time)}</td></tr>` : ''}
          </tbody>
        </table>
      </div>
    `).join('');
    const exerciseName = exercise?.exercise_name || exercise?.scenario_name || '';
    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8" />
          <title>MSEL Report${exerciseName ? ' — ' + escapeHTML(exerciseName) : ''}</title>
          <style>
            * { box-sizing: border-box; }
            body { font-family: Arial, Helvetica, sans-serif; color: #111; margin: 24px; }
            h1 { color: #ea580c; margin: 0 0 4px 0; font-size: 22px; }
            .subtitle { color: #555; margin-bottom: 16px; font-size: 13px; }
            .meta { color: #333; font-size: 12px; margin-bottom: 20px; }
            .event { border: 1px solid #ccc; border-radius: 6px; padding: 12px 16px; margin-bottom: 14px; page-break-inside: avoid; }
            .event-head { display: flex; align-items: flex-start; gap: 12px; margin-bottom: 8px; }
            .event-num { flex: 0 0 auto; width: 42px; height: 42px; border-radius: 50%; background: #fff2e6; color: #c2410c; display: inline-flex; align-items: center; justify-content: center; font-weight: 700; font-size: 14px; }
            .event-title { flex: 1 1 auto; }
            .event-title h2 { margin: 0 0 4px 0; font-size: 16px; color: #111; }
            .badges { display: flex; flex-wrap: wrap; gap: 6px; }
            .badge { display: inline-block; padding: 2px 8px; border-radius: 999px; font-size: 11px; border: 1px solid #ddd; background: #f7f7f7; color: #333; }
            .badge-type { background: #eef4ff; color: #1d4ed8; border-color: #c7d7fe; }
            .badge-mode { background: #f3f4f6; color: #374151; border-color: #d1d5db; }
            .badge-done { background: #e7f7ec; color: #0f766e; border-color: #bbe6cd; }
            table.event-table { width: 100%; border-collapse: collapse; font-size: 12px; margin-top: 6px; }
            table.event-table th, table.event-table td { text-align: left; vertical-align: top; padding: 6px 8px; border: 1px solid #e5e7eb; }
            table.event-table th { width: 180px; background: #f9fafb; color: #444; font-weight: 600; }
            .footer { color: #666; font-size: 11px; margin-top: 8px; text-align: right; }
            @media print { body { margin: 12mm; } .event { break-inside: avoid; } }
          </style>
        </head>
        <body>
          <h1>Master Scenario Events List (MSEL)</h1>
          ${exerciseName ? `<div class="subtitle">Exercise: ${escapeHTML(exerciseName)}</div>` : ''}
          <div class="meta">Total events: ${mselInjections.length} &nbsp;|&nbsp; Printed: ${escapeHTML(new Date().toLocaleString([], { hour12: false }))}</div>
          ${rowsHTML}
          <div class="footer">EXRSIM — MSEL Report</div>
        </body>
      </html>
    `;
    writeSanitizedHTML(printWindow.document, printContent);
    printWindow.focus();
    setTimeout(() => printWindow.print(), 300);
  };

  // Fetch MSEL injections on component mount for the Events count
  useEffect(() => {
    const fetchInjections = async () => {
      if (!exercise?.id) return;
      try {
        const headers = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
        const response = await axios.get(`${API}/msel/${exercise.id}`, headers);
        const injections = response.data || [];
        const sortedInjections = injections.sort((a, b) => a.event_number - b.event_number);
        setMselInjections(sortedInjections);
      } catch (error) {
        console.error('Error fetching MSEL injections for count:', error);
        setMselInjections([]);
      }
    };
    fetchInjections();
  }, [exercise?.id, token]);

  // Print function for complete exercise report
  const handlePrintCompleteExercise = async () => {
    // Immediate alert to confirm function is called
    alert('Print button clicked! Check console for details.');
    
    try {
      console.log('=== PRINT FUNCTION START (Exercise Detail View) ===');
      console.log('Exercise data:', exercise);
      console.log('Exercise ID:', exercise.id);
      console.log('API constant:', API);
      console.log('Token available:', !!token);
      
      if (!exercise.id) {
        toast.error('Exercise ID not found');
        return;
      }
      
      // Fetch all exercise data including goals, objectives, events, etc.
      let fullExerciseData = {};
      let injections = [];
      let coordinators = [];
      
      const headers = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
      
      console.log('=== FETCHING FULL EXERCISE DATA ===');
      try {
        const exerciseResponse = await axios.get(`${API}/exercise-builder/${exercise.id}`, headers);
        fullExerciseData = exerciseResponse.data || {};
        console.log('✅ Fetched full exercise data');
        console.log('Full exercise data:', fullExerciseData);
        console.log('Goals in data:', fullExerciseData.goals);
        console.log('Objectives in data:', fullExerciseData.objectives);
      } catch (error) {
        console.error('❌ Error fetching exercise data:', error);
        console.error('Error details:', error.response?.data);
        console.warn('⚠️ Exercise data not found, but will continue to fetch MSEL data');
        // Don't return here - MSEL data is in a separate collection and may still exist
        fullExerciseData = {}; // Use empty object as fallback
      }
      
      console.log('=== FETCHING MSEL INJECTIONS ===');
      console.log('Exercise ID to fetch:', exercise.id);
      console.log('Full API URL:', `${API}/msel/${exercise.id}`);
      console.log('Using token:', token ? 'Yes' : 'No');
      
      try {
        const mselResponse = await axios.get(`${API}/msel/${exercise.id}`, headers);
        injections = mselResponse.data || [];
        console.log('✅ Fetched MSEL injections:', injections.length, 'events');
        console.log('Injections data:', injections);
        
        if (injections.length === 0) {
          console.warn('⚠️ No injections found for this exercise!');
        } else {
          console.log('First injection:', injections[0]);
          console.log('First injection exercise_id:', injections[0]?.exercise_id);
          console.log('Expected exercise_id:', exercise.id);
        }
      } catch (error) {
        console.error('❌ Error fetching MSEL:', error);
        console.error('Error details:', error.response?.data);
        console.error('Status:', error.response?.status);
        injections = [];
      }
      
      console.log('=== FETCHING COORDINATORS ===');
      try {
        const coordResponse = await axios.get(`${API}/team-coordinators/${exercise.id}`, headers);
        coordinators = coordResponse.data || [];
        console.log('✅ Fetched coordinators:', coordinators.length, 'coordinators');
        console.log('Coordinators data:', coordinators);
      } catch (error) {
        console.error('❌ Error fetching coordinators:', error);
        console.error('Error details:', error.response?.data);
        coordinators = [];
      }
      
      console.log('=== DATA BEFORE PRINT GENERATION ===');
      console.log('Full exercise data available:', !!fullExerciseData);
      console.log('Goals count:', (fullExerciseData.goals || []).length);
      console.log('Objectives count:', (fullExerciseData.objectives || []).length);
      console.log('Injections count:', injections.length);
      console.log('Injections variable:', injections);
      console.log('Coordinators count:', coordinators.length);
      
      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        toast.error('Please allow pop-ups to print the exercise report');
        return;
      }
      
      console.log('Print window opened, generating content...');
      
      // Generate print content using the full exercise data
      const printContent = generatePrintContentForDetailView(fullExerciseData, injections, coordinators);
      
      console.log('Content generated, writing to window...');
      writeSanitizedHTML(printWindow.document, printContent);
      console.log('Content written, triggering print...');
      
      setTimeout(() => {
        printWindow.print();
      }, 250);
    } catch (error) {
      console.error('Error in print function:', error);
      toast.error('Error generating print report: ' + error.message);
    }
  };
  
  // Helper function to generate print content (similar to ExerciseBuilder's version)
  const generatePrintContentForDetailView = (exerciseData, injectionsData = [], coordinatorsData = []) => {
    const goals = exerciseData.goals || [];
    const objectives = exerciseData.objectives || [];
    const events = exerciseData.events || [];
    const functions = exerciseData.functions || [];
    const organizations = exerciseData.organizations || [];
    const codeWords = exerciseData.codeWords || [];
    const callsigns = exerciseData.callsigns || [];
    const frequencies = exerciseData.frequencies || [];
    const assumptions = exerciseData.assumptions || [];
    const artificialities = exerciseData.artificialities || [];
    const safetyConcerns = exerciseData.safetyConcerns || [];
    
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>${exerciseData.exercise_name || 'Exercise'} - Complete Report</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 40px; color: #333; }
          h1 { color: #f97316; border-bottom: 3px solid #f97316; padding-bottom: 10px; }
          h2 { color: #f97316; margin-top: 30px; border-bottom: 2px solid #f97316; padding-bottom: 5px; }
          h3 { color: #444; margin-top: 20px; }
          .section { margin-bottom: 30px; page-break-inside: avoid; }
          .field { margin: 10px 0; }
          .label { font-weight: bold; color: #666; }
          .value { margin-left: 10px; }
          table { width: 100%; border-collapse: collapse; margin: 15px 0; }
          th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
          th { background-color: #f97316; color: white; }
          .item { background: #f9f9f9; padding: 10px; margin: 10px 0; border-left: 4px solid #f97316; }
          @media print { 
            body { margin: 20px; }
            .section { page-break-inside: avoid; }
          }
        </style>
      </head>
      <body>
        <h1>${exerciseData.exercise_name || 'Exercise Report'}</h1>
        
        <div class="section">
          <h2>Step 1: Exercise Information</h2>
          <div class="field"><span class="label">Exercise Name:</span><span class="value">${exerciseData.exercise_name || 'N/A'}</span></div>
          <div class="field"><span class="label">Exercise Type:</span><span class="value">${exerciseData.exercise_type || 'N/A'}</span></div>
          <div class="field"><span class="label">Description:</span><span class="value">${exerciseData.exercise_description || 'N/A'}</span></div>
          <div class="field"><span class="label">Location:</span><span class="value">${exerciseData.location || 'N/A'}</span></div>
          <div class="field"><span class="label">Coordinates:</span><span class="value">${exerciseData.latitude || 0}, ${exerciseData.longitude || 0}</span></div>
          <div class="field"><span class="label">Start:</span><span class="value">${exerciseData.start_date ? new Date(exerciseData.start_date).toLocaleDateString() : 'N/A'} at ${exerciseData.start_time || 'N/A'}</span></div>
          <div class="field"><span class="label">End:</span><span class="value">${exerciseData.end_date ? new Date(exerciseData.end_date).toLocaleDateString() : 'N/A'} at ${exerciseData.end_time || 'N/A'}</span></div>
          <div class="field"><span class="label">Zoom Level:</span><span class="value">${exerciseData.zoom_level || 'N/A'}</span></div>
        </div>

        <div class="section">
          <h2>Step 2: Scope</h2>
          <div class="field"><span class="label">Description:</span><span class="value">${exerciseData.scope_description || 'N/A'}</span></div>
          <div class="field"><span class="label">Hazards:</span><span class="value">${exerciseData.scope_hazards || 'N/A'}</span></div>
          <div class="field"><span class="label">Geographic Area:</span><span class="value">${exerciseData.scope_geographic_area || 'N/A'}</span></div>
          <div class="field"><span class="label">Functions:</span><span class="value">${exerciseData.scope_functions || 'N/A'}</span></div>
          <div class="field"><span class="label">Organizations:</span><span class="value">${exerciseData.scope_organizations || 'N/A'}</span></div>
          <div class="field"><span class="label">Personnel:</span><span class="value">${exerciseData.scope_personnel || 'N/A'}</span></div>
        </div>

        <div class="section">
          <h2>Step 3: Purpose</h2>
          <div class="field"><span class="label">Purpose Description:</span><span class="value">${exerciseData.purpose_description || 'N/A'}</span></div>
        </div>

        <div class="section">
          <h2>Step 4: Scenario</h2>
          <div class="field"><span class="label">Scenario Name:</span><span class="value">${exerciseData.scenario_name || 'N/A'}</span></div>
          <div class="field"><span class="label">Description:</span><span class="value">${exerciseData.scenario_description || 'N/A'}</span></div>
        </div>

        <div class="section">
          <h2>Step 5: Goals</h2>
          ${goals.length > 0 ? goals.map((goal, idx) => `
            <div class="item">
              <strong>Goal ${idx + 1}:</strong> ${goal.name}<br/>
              <span class="label">Description:</span> ${goal.description || 'N/A'}<br/>
              <span class="label">Achieved:</span> ${goal.achieved || 'No'}
            </div>
          `).join('') : '<p>No goals defined</p>'}
        </div>

        <div class="section">
          <h2>Step 6: Objectives</h2>
          ${objectives.length > 0 ? objectives.map((obj, idx) => `
            <div class="item">
              <strong>Objective ${idx + 1}:</strong> ${obj.name}<br/>
              <span class="label">Description:</span> ${obj.description || 'N/A'}<br/>
              <span class="label">Achieved:</span> ${obj.achieved || 'No'}
            </div>
          `).join('') : '<p>No objectives defined</p>'}
        </div>

        <div class="section">
          <h2>Step 7: Functions</h2>
          ${functions.length > 0 ? functions.map((func, idx) => `
            <div class="item">
              <strong>Function ${idx + 1}:</strong> ${func.name}<br/>
              <span class="label">Description:</span> ${func.description || 'N/A'}
            </div>
          `).join('') : '<p>No functions defined</p>'}
        </div>

        <div class="section">
          <h2>Step 8: Injections (MSEL)</h2>
          ${injectionsData.length > 0 ? injectionsData.map((inj, idx) => `
            <div class="item">
              <strong>Injection ${idx + 1}:</strong><br/>
              <span class="label">Event Number:</span> ${inj.event_number || 'N/A'}<br/>
              <span class="label">Scenario Time:</span> ${inj.scenario_time || 'N/A'}<br/>
              <span class="label">Event Type:</span> ${inj.event_type || 'N/A'}<br/>
              <span class="label">Inject Mode:</span> ${inj.inject_mode || 'N/A'}<br/>
              <span class="label">From:</span> ${inj.from_entity || 'N/A'}<br/>
              <span class="label">To:</span> ${inj.to_entity || 'N/A'}<br/>
              <span class="label">Message:</span> ${inj.message || 'N/A'}<br/>
              <span class="label">Expected Response:</span> ${inj.expected_response || 'N/A'}<br/>
              <span class="label">Objective/Capability/Task:</span> ${inj.objective_capability_task || 'N/A'}
            </div>
          `).join('') : '<p>No MSEL injections defined</p>'}
        </div>

        <div class="section">
          <h2>Step 9: Organizations</h2>
          ${organizations.length > 0 ? organizations.map((org, idx) => `
            <div class="item">
              <strong>Organization ${idx + 1}:</strong> ${org.name}<br/>
              <span class="label">Description:</span> ${org.description || 'N/A'}<br/>
              <span class="label">Home Base:</span> ${org.home_base || 'N/A'}<br/>
              <span class="label">Contact:</span> ${org.contact_first_name || ''} ${org.contact_last_name || ''} - ${org.contact_phone || 'N/A'}
            </div>
          `).join('') : '<p>No organizations defined</p>'}
        </div>

        <div class="section">
          <h2>Step 10: Team Coordinators</h2>
          ${coordinatorsData.length > 0 ? `
            <table>
              <thead>
                <tr>
                  <th>Team</th>
                  <th>Name</th>
                  <th>Phone</th>
                  <th>Radio</th>
                </tr>
              </thead>
              <tbody>
                ${coordinatorsData.map(coord => `
                  <tr>
                    <td>${coord.team_name || 'N/A'}</td>
                    <td>${coord.coordinator_name || 'N/A'}</td>
                    <td>${coord.phone || 'N/A'}</td>
                    <td>${coord.radio_channel || 'N/A'}</td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
          ` : '<p>No team coordinators defined</p>'}
        </div>

        <div class="section">
          <h2>Step 11: Code Words</h2>
          ${codeWords.length > 0 ? codeWords.map((cw, idx) => `
            <div class="item">
              <strong>${cw.word}</strong><br/>
              <span class="label">Meaning:</span> ${cw.meaning || 'N/A'}
            </div>
          `).join('') : '<p>No code words defined</p>'}
        </div>

        <div class="section">
          <h2>Step 12: Callsigns</h2>
          ${callsigns.length > 0 ? callsigns.map((cs, idx) => `
            <div class="item">
              <strong>${cs.callsign}</strong><br/>
              <span class="label">Description:</span> ${cs.description || 'N/A'}
            </div>
          `).join('') : '<p>No callsigns defined</p>'}
        </div>

        <div class="section">
          <h2>Step 13: Assumptions</h2>
          ${assumptions.length > 0 ? assumptions.map((assum, idx) => `
            <div class="item">
              <strong>${assum.assumption}</strong><br/>
              <span class="label">Description:</span> ${assum.description || 'N/A'}
            </div>
          `).join('') : '<p>No assumptions defined</p>'}
        </div>

        <div class="section">
          <h2>Step 14: Artificialities</h2>
          ${artificialities.length > 0 ? artificialities.map((art, idx) => `
            <div class="item">
              <strong>${art.artificiality}</strong><br/>
              <span class="label">Description:</span> ${art.description || 'N/A'}
            </div>
          `).join('') : '<p>No artificialities defined</p>'}
        </div>

        <div class="section">
          <h2>Step 15: Safety Concerns</h2>
          ${safetyConcerns.length > 0 ? safetyConcerns.map((safety, idx) => `
            <div class="item">
              <strong>${safety.concern}</strong><br/>
              <span class="label">Safety Officer:</span> ${safety.safety_officer || 'N/A'}<br/>
              <span class="label">Phone:</span> ${safety.phone || 'N/A'}<br/>
              <span class="label">Description:</span> ${safety.description || 'N/A'}
            </div>
          `).join('') : '<p>No safety concerns defined</p>'}
        </div>

        <div class="section">
          <h2>Step 16: Mapping</h2>
          <p><em>Map objects and markers are available in the interactive mapping interface.</em></p>
        </div>

      </body>
      </html>
    `;
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="mb-6">
        <Button 
          variant="ghost" 
          onClick={onBack}
          className="text-gray-400 hover:text-orange-500 mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Exercise List
        </Button>
        <div className="flex items-start space-x-6">
          {exercise.exercise_image ? (
            <div className="w-32 h-32 rounded-lg bg-gray-700 overflow-hidden flex-shrink-0">
              <img 
                src={exercise.exercise_image} 
                alt={exercise.exercise_name} 
                className="w-full h-full object-cover"
              />
            </div>
          ) : hiraImage ? (
            <div
              className="w-32 h-32 rounded-lg bg-gray-700 overflow-hidden flex-shrink-0"
              data-testid="exercise-hira-header-image"
              title="Hazard image from the linked HIRA entry"
            >
              <img 
                src={hiraImage} 
                alt={exercise.exercise_name} 
                className="w-full h-full object-cover"
              />
            </div>
          ) : null}
          <div className="flex-1">
            <h1 className="text-3xl font-bold text-orange-500 mb-2">{exercise.exercise_name}</h1>
            <div className="flex items-center space-x-3 mb-4">
              <Badge className="bg-blue-500/20 text-blue-300 border-blue-500/30 text-base px-3 py-1">
                {exercise.exercise_type}
              </Badge>
            </div>
            <p className="text-gray-300 text-lg">{exercise.exercise_description}</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Basic Information */}
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader>
            <CardTitle className="text-orange-500 flex items-center">
              <MapPin className="h-5 w-5 mr-2" />
              Location & Schedule
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <Label className="text-gray-400 text-sm">Location</Label>
              <p className="text-white">{exercise.location || 'Not specified'}</p>
            </div>
            <div>
              <Label className="text-gray-400 text-sm">Start</Label>
              <p className="text-white">
                {new Date(exercise.start_date).toLocaleDateString()} at {exercise.start_time || 'TBD'}
              </p>
            </div>
            <div>
              <Label className="text-gray-400 text-sm">End</Label>
              <p className="text-white">
                {new Date(exercise.end_date).toLocaleDateString()} at {exercise.end_time || 'TBD'}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Scope */}
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader>
            <CardTitle className="text-orange-500">Exercise Scope</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div>
              <Label className="text-gray-400 text-sm">Hazards</Label>
              <p className="text-white text-sm">{exercise.scope_hazards || 'Not specified'}</p>
            </div>
            <div>
              <Label className="text-gray-400 text-sm">Geographic Area</Label>
              <p className="text-white text-sm">{exercise.scope_geographic_area || 'Not specified'}</p>
            </div>
            <div>
              <Label className="text-gray-400 text-sm">Functions</Label>
              <p className="text-white text-sm">{exercise.scope_functions || 'Not specified'}</p>
            </div>
          </CardContent>
        </Card>

        {/* Purpose */}
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader>
            <CardTitle className="text-orange-500">Exercise Purpose</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-white">{exercise.purpose_description || 'Not specified'}</p>
          </CardContent>
        </Card>

        {/* Scenario */}
        {(exercise.scenario_name || exercise.scenario_description) && (
          <Card className="bg-gray-800 border-gray-700 col-span-full">
            <CardHeader>
              <CardTitle className="text-orange-500">Scenario</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-start space-x-4">
                {exercise.scenario_image && (
                  <div className="w-24 h-24 rounded bg-gray-700 overflow-hidden flex-shrink-0">
                    <img 
                      src={exercise.scenario_image} 
                      alt={exercise.scenario_name} 
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-white mb-2">{exercise.scenario_name}</h3>
                  <p className="text-gray-300">{exercise.scenario_description}</p>
                  {(exercise.scenario_latitude || exercise.scenario_longitude) && (
                    <p className="text-gray-400 text-sm mt-2">
                      Coordinates: {exercise.scenario_latitude}, {exercise.scenario_longitude}
                    </p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      <div className="mt-8 flex justify-end space-x-4">
        <Button
          variant="outline"
          className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
          onClick={handlePrintCompleteExercise}
        >
          <Printer className="h-4 w-4 mr-2" />
          Print Complete Exercise
        </Button>
        <Button
          variant="outline"
          className="border-orange-500/50 text-orange-500 hover:bg-orange-500/10"
          onClick={() => window.location.hash = `#builder?exercise=${exercise.id}`}
        >
          <Edit className="h-4 w-4 mr-2" />
          Edit Exercise
        </Button>
        <Button
          variant="outline"
          className="border-green-500/50 text-green-400 hover:bg-green-500/10"
          onClick={handleViewMSEL}
        >
          <ClipboardList className="h-4 w-4 mr-2" />
          View MSEL
        </Button>
      </div>

      {/* MSEL Injections Modal */}
      {showMSELModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[10001] p-4 overflow-y-auto">
          <div className="bg-gray-800 rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-700">
              <div>
                <h2 className="text-2xl font-bold text-orange-500 flex items-center">
                  <ClipboardList className="h-6 w-6 mr-3" />
                  MSEL Injections - {exercise.scenario_name}
                </h2>
                <p className="text-gray-400 text-sm mt-1">
                  Master Scenario Events List (Step 8: Injections)
                </p>
              </div>
              <button
                onClick={() => setShowMSELModal(false)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {loadingMSEL ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
                    <p className="text-gray-400">Loading MSEL injections...</p>
                  </div>
                </div>
              ) : mselInjections.length === 0 ? (
                <div className="text-center py-12">
                  <ClipboardList className="h-16 w-16 text-gray-600 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-gray-400 mb-2">No Injections Found</h3>
                  <p className="text-gray-500">
                    This exercise doesn't have any MSEL injections yet.
                  </p>
                  <p className="text-gray-500 text-sm mt-2">
                    Create injections in Step 8 of the Exercise Builder.
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Summary Stats */}
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                    <div className="bg-gray-900 rounded-lg p-4 border border-gray-700">
                      <div className="text-gray-400 text-sm">Total Injections</div>
                      <div className="text-2xl font-bold text-white mt-1">{mselInjections.length}</div>
                    </div>
                    <div className="bg-gray-900 rounded-lg p-4 border border-gray-700">
                      <div className="text-gray-400 text-sm">Event Types</div>
                      <div className="text-2xl font-bold text-white mt-1">
                        {new Set(mselInjections.map(i => i.event_type)).size}
                      </div>
                    </div>
                    <div className="bg-gray-900 rounded-lg p-4 border border-gray-700">
                      <div className="text-gray-400 text-sm">Inject Modes</div>
                      <div className="text-2xl font-bold text-white mt-1">
                        {new Set(mselInjections.map(i => i.inject_mode)).size}
                      </div>
                    </div>
                    <div className="bg-gray-900 rounded-lg p-4 border border-gray-700">
                      <div className="text-gray-400 text-sm">Completed</div>
                      <div className="text-2xl font-bold text-green-400 mt-1">
                        {mselInjections.filter(i => i.completed).length}/{mselInjections.length}
                      </div>
                    </div>
                  </div>

                  {/* Injections List */}
                  <div className="space-y-3">
                    {mselInjections.map((injection, index) => (
                      <div
                        key={injection.id}
                        className={`bg-gray-900 rounded-lg border ${
                          injection.completed ? 'border-green-500/30' : 'border-gray-700'
                        } overflow-hidden`}
                      >
                        {/* Injection Header */}
                        <div className={`px-4 py-3 ${
                          injection.completed ? 'bg-green-500/10' : 'bg-gray-800'
                        } border-b border-gray-700 flex items-center justify-between`}>
                          <div className="flex items-center space-x-4">
                            <div className={`px-3 py-1 rounded ${
                              injection.completed ? 'bg-green-500/20 text-green-400' : 'bg-orange-500/20 text-orange-400'
                            } font-bold text-sm`}>
                              #{injection.event_number}
                            </div>
                            <div>
                              <span className="text-white font-semibold">{injection.event_type || 'No Event Type'}</span>
                              <span className="text-gray-400 text-sm ml-3">
                                ⏰ {injection.scenario_time || 'Not Set'}
                              </span>
                            </div>
                          </div>
                          {injection.completed && (
                            <div className="flex items-center text-green-400 text-sm">
                              <CheckCircle className="h-4 w-4 mr-1" />
                              Completed
                            </div>
                          )}
                        </div>

                        {/* Injection Details */}
                        <div className="p-4 space-y-3">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <div className="text-gray-400 text-xs uppercase mb-1">Inject Mode</div>
                              <div className="text-white text-sm">{injection.inject_mode || 'Not Set'}</div>
                            </div>
                            <div>
                              <div className="text-gray-400 text-xs uppercase mb-1">From → To</div>
                              <div className="text-white text-sm">
                                {injection.from_entity || 'Not Set'} → {injection.to_entity || 'Not Set'}
                              </div>
                            </div>
                          </div>

                          <div>
                            <div className="text-gray-400 text-xs uppercase mb-1">Message</div>
                            <div className="text-white text-sm bg-gray-800 rounded p-3 border border-gray-700">
                              {injection.message || 'No message'}
                            </div>
                          </div>

                          <div>
                            <div className="text-gray-400 text-xs uppercase mb-1">Expected Response</div>
                            <div className="text-green-400 text-sm bg-gray-800 rounded p-3 border border-gray-700">
                              {injection.expected_response}
                            </div>
                          </div>

                          <div>
                            <div className="text-gray-400 text-xs uppercase mb-1">Objective/Capability/Task</div>
                            <div className="text-blue-400 text-sm bg-gray-800 rounded p-3 border border-gray-700">
                              {injection.objective_capability_task}
                            </div>
                          </div>

                          {injection.notes && (
                            <div>
                              <div className="text-gray-400 text-xs uppercase mb-1">Notes</div>
                              <div className="text-gray-300 text-sm bg-gray-800 rounded p-3 border border-gray-700">
                                {injection.notes}
                              </div>
                            </div>
                          )}

                          {injection.actual_time && (
                            <div>
                              <div className="text-gray-400 text-xs uppercase mb-1">Actual Time</div>
                              <div className="text-white text-sm">⏱️ {injection.actual_time}</div>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-700">
              <Button
                variant="outline"
                onClick={handlePrintMSEL}
                disabled={loadingMSEL || mselInjections.length === 0}
                className="border-orange-500/50 text-orange-400 hover:bg-orange-500/10 disabled:opacity-50"
                data-testid="print-msel-modal-btn"
              >
                <Printer className="h-4 w-4 mr-2" />
                Print MSEL
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowMSELModal(false)}
                className="border-gray-600 text-gray-300 hover:bg-gray-700"
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


export default ExerciseDetailView;
